// Case study copy — used by the React pages AND scripts/prerender.mjs.
const SITE_URL = "https://gadgetguymedia.com";

const fhcog = {
  slug: "family-health-care-gallatin",
  path: "/case-studies/family-health-care-gallatin",
  projectSlug: "family-health-care-of-gallatin",
  title: "Family Health Care of Gallatin: AI Front Desk Case Study",
  description:
    "How a Gallatin, TN primary care clinic got a 24/7 AI front desk, a 97 desktop speed score and AI-readable service pages. Start at gadgetguymedia.com.",
  ogImage: `${SITE_URL}/brand-v2/favicon/og-case-fhcog.png`,
  datePublished: "2026-10-21",
  card: "Phone-only answers and a slow, single-page site → a 24/7 AI front desk, six AI-readable service pages and an audit score of 66 → 85 in three days.",
  h1: "How Family Health Care of Gallatin Got a 24/7 AI Front Desk and a Faster, AI-Ready Website",
  inShort:
    "A Gallatin, TN primary care clinic went from phone-only answers and a slow, single-page site to a 24/7 AI front desk, six AI-readable service pages, and a site that scored 66 to 85 on our Free Local AI Audit in three days.",
  facts: [
    ["Client", "Family Health Care of Gallatin (fhcog.net), a primary care clinic at 831B Nashville Pike, Gallatin, TN"],
    ["Services", "Free Local AI Audit, AI Front Desk, website fixes"],
    ["Timeline", "Three days, October 2026"],
  ],
  stats: [
    ["66 → 85", "Audit score"],
    ["82 → 97", "Desktop speed"],
    ["61%", "Lighter page"],
    ["24/7", "AI front desk"],
  ],
  challengeIntro:
    "Family Health Care of Gallatin had a solid foundation, but our Free Local AI Audit found patients slipping away:",
  challenge: [
    ["No answers after hours.", "Booking was phone-only, and the office stops answering at 4:30 PM Monday through Thursday and 1:00 PM Friday. After that, the options were voicemail or a form promising a reply during business hours."],
    ["A slow mobile site.", "In a mobile lab test, the first content took 10.3 seconds to appear. The page weighed 2.5 MB."],
    ["Services AI tools couldn't find.", "The clinic offers DOT/CDL physicals, diabetes care, mental health and weight loss care, but had no service pages, and inner pages showed the homepage's content to tools that don't run JavaScript."],
    ["The phone wasn't the main button.", "\"Explore Services\" led the banner, and on mobile the phone number hid behind the menu."],
  ],
  auditStep:
    "We started with a read-only review of speed, contact info, mobile, Google visibility and AI search visibility, with a plain-language grade for each area and a prioritized fix list.",
  frontDesk:
    "The clinic now has a 24/7 AI chat assistant on every page. In our tests it answered questions about hours, services, insurance, forms and payments in English and Spanish. It stays out of medical advice: asked about chest pain, it told the patient to call 911, and it pointed a refill request to the patient portal or phone.",
  privacyIntro: "We were clear about privacy from the start:",
  privacy: [
    ["The chat assistant is not HIPAA compliant", "and isn't covered by a BAA. It's for non-medical questions only, and patients are told not to include any protected health information (PHI) in chat."],
    "Chats are not saved.",
    "The clinic's Google Workspace, where the patient forms live, is covered by a signed Business Associate Agreement (BAA) with Google.",
    "Contact form messages are deleted after 30 days.",
    "The chat panel and contact form are excluded from website session recording.",
  ],
  fixes: [
    ["Speed:", "smaller images, and the spam-protection script now loads only with the contact form."],
    ["Calls to action:", "\"Call (615) 206-0500\" is the main banner button, with a sticky \"Call to book\" bar on mobile."],
    ["Service pages:", "six dedicated pages (DOT/CDL physicals, walk-in sick visits, diabetes care, mental health, weight loss and women's health), each with hours, what to bring, the provider and FAQs."],
    ["AI and search readiness:", "every page now serves its own title, description and content to search engines and AI tools without JavaScript, backed by structured data, an llms.txt file and a 12-page sitemap."],
    ["Consistency and new pages:", "the address reads \"831B Nashville Pike\" everywhere on the site, new /contact and /privacy pages went live, and online paperwork replaced a print-only scanned packet."],
  ],
  results: [
    ["Free Local AI Audit score", "66 / 100", "85 / 100"],
    ["Desktop speed (Lighthouse)", "82", "97"],
    ["Page weight", "2.5 MB", "1.0 MB (about 61% lighter)"],
    ["Mobile first content (lab test)", "10.3 s", "4.4 s"],
    ["Accessibility (Lighthouse)", "84–88", "94"],
    ["After-hours answers", "None", "24/7 AI front desk chat"],
    ["Dedicated service pages", "0", "6, readable by AI tools"],
    ["Pages serving their own title and content without JavaScript", "Homepage only", "Every page"],
    ["Phone as the main call to action", "Secondary button", "Main button and sticky \"Call to book\" bar"],
    ["Contact and privacy pages", "None", "Live"],
    ["\"primary care Gallatin TN\" search", "No baseline", "#1"],
    ["\"walk-in clinic Gallatin TN … accepting new patients\" search", "#1", "Held #1"],
  ],
  resultsNote:
    "Best Practices and SEO stayed at 100. Search results update slowly, so we'll re-check rankings in late November. A few listing and redirect cleanups are ongoing. All speed numbers are Lighthouse lab tests run with the same engine and settings before and after.",
  why: [
    "Most people look for a doctor on their phone, often after hours. If the site is slow or the only option is voicemail, they move on.",
    "AI search raises the stakes. ChatGPT, Perplexity and Google's AI answers can only recommend what they can read, and a clear page per service gives them something specific to point to.",
    "For healthcare, privacy matters just as much. An AI front desk can handle routine questions about new patients, insurance, hours and forms, as long as it's clear about its limits: non-medical questions only, no health information in chat, and anything clinical goes to the phone or portal.",
  ],
  ctaText:
    "Start with a Free Local AI Audit to get a plain-language scorecard and a prioritized fix list. Ready for after-hours answers? Our AI Front Desk is $1,500, trained only on your practice's own hours, services, insurance and FAQs.",
  faqs: [
    { q: "What is a Free Local AI Audit?", a: "It's a free, read-only review of a local business website by Gadget Guy Media in Gallatin, TN. It grades speed, mobile, calls to action, Google visibility and AI search visibility, then lists fixes in priority order." },
    { q: "Can an AI front desk work for a medical practice?", a: "Yes, for routine, non-medical questions. At Family Health Care of Gallatin, it answers hours, insurance, services and forms in English and Spanish, and points refill requests to the patient portal." },
    { q: "Is the chat assistant HIPAA compliant?", a: "No. It isn't covered by a BAA, so patients are told not to include any protected health information (PHI) in chat, and chats aren't saved. The clinic's BAA with Google covers only its Google Workspace, where the patient forms live." },
    { q: "How much did Family Health Care of Gallatin's site improve?", a: "Its audit score went from 66 to 85 out of 100, and desktop Lighthouse speed went from 82 to 97. The page got about 61% lighter." },
  ],
};

fhcog.schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Article",
      "@id": `${SITE_URL}${fhcog.path}#article`,
      headline: fhcog.h1,
      description: fhcog.description,
      image: fhcog.ogImage,
      datePublished: fhcog.datePublished,
      dateModified: fhcog.datePublished,
      mainEntityOfPage: `${SITE_URL}${fhcog.path}`,
      author: { "@type": "Organization", "@id": `${SITE_URL}/#organization`, name: "Gadget Guy Media" },
      publisher: { "@type": "Organization", "@id": `${SITE_URL}/#organization`, name: "Gadget Guy Media", url: SITE_URL },
      about: { "@type": "MedicalClinic", name: "Family Health Care of Gallatin", url: "https://fhcog.net",
        address: { "@type": "PostalAddress", streetAddress: "831B Nashville Pike", addressLocality: "Gallatin", addressRegion: "TN", addressCountry: "US" } },
    },
    {
      "@type": "FAQPage",
      "@id": `${SITE_URL}${fhcog.path}#faq`,
      url: `${SITE_URL}${fhcog.path}`,
      mainEntity: fhcog.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Case Studies", item: `${SITE_URL}/case-studies` },
        { "@type": "ListItem", position: 3, name: "Family Health Care of Gallatin", item: `${SITE_URL}${fhcog.path}` },
      ],
    },
  ],
};

export const CASE_STUDIES = [fhcog];

export const caseStudiesIndex = {
  path: "/case-studies",
  title: "Case Studies — Gadget Guy Media · Gallatin, TN",
  description:
    "Real before-and-after results from Gadget Guy Media clients in Gallatin and Sumner County, TN: Free Local AI Audits, AI Front Desk chatbots and website fixes.",
  h1: "Case Studies",
  lede: "Real local businesses, real before-and-after numbers. Every result here was measured the same way before and after.",
};

export const findCaseStudy = (slug) => CASE_STUDIES.find((c) => c.slug === slug);
export const caseStudyForProject = (projectSlug) => CASE_STUDIES.find((c) => c.projectSlug === projectSlug);
